import { Component } from "@theme/component";
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle
} from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

const HOVER_COMMIT_DELAY_MS = 150;

class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  #submenuMutationObserver = null;
  #hoverDispatchTimer;

  #state = {
    activeItem: null,
    activeOverflowItem: null
  };

  #pointerIdleTimer;

  #lastPointer = {
    x: 0,
    y: 0
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener("resize", this.#resizeListener);
    this.addEventListener("keydown", this.#onKeydown);

    this.overflowMenu?.addEventListener(
      "pointerleave",
      this.#overflowSubmenuListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener("resize", this.#resizeListener);
    this.removeEventListener("keydown", this.#onKeydown);

    document.body.removeEventListener(
      "pointermove",
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener(
      "pointerleave",
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #onKeydown = (event) => {
    if (event.key !== "Escape") return;

    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);

    this.#deactivate(item, { force: true });
    control.focus();
  };

  toggle = (event) => {
    const button = event.target;

    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    if (button.getAttribute("aria-expanded") === "true") {
      this.#deactivate(
        this.#state.activeItem ?? item,
        { force: true }
      );

      const focusTarget = this.#focusTargetAfterClose(item);

      if (focusTarget !== button) {
        focusTarget.focus();
      }
    } else {
      this.activate(event);
    }
  };

  #focusTargetAfterClose(item) {
    const listItem = item.closest(".menu-list__list-item");

    const slot = listItem instanceof HTMLElement
      ? listItem.slot
      : "";

    if (slot === "overflow" || slot === "more") {
      const moreButton = this.querySelector(
        '[slot="more"] [ref="menuitem"]'
      );

      if (moreButton instanceof HTMLElement) {
        return moreButton;
      }
    }

    return this.#expandableFor(item);
  }

  #expandableFor(item) {
    const disclosure = item
      .closest(".menu-list__list-item")
      ?.querySelector('[ref="disclosure[]"]');

    return disclosure instanceof HTMLElement
      ? disclosure
      : item;
  }

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      event.movementY >= 1;

    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = "false";
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const listItem = target.closest(".menu-list__list-item");

      if (
        listItem &&
        !listItem.contains(this.#state.activeItem)
      ) {
        listItem.dispatchEvent(
          new PointerEvent("pointerenter", {
            bubbles: false
          })
        );
      }
    });
  }

  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener(
        "pointermove",
        this.#onPointerMove
      );
    }

    const rect = item.getBoundingClientRect();

    const boundary = this.headerComponent?.hasAttribute(
      "data-submenu-overlap-bottom-row"
    )
      ? this.headerComponent?.querySelector(".header__row--top")
      : this.headerComponent;

    item.style.setProperty(
      "--box-height",
      `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`
    );
  }

  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;

    item.style.removeProperty("--box-height");
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector(
      '[part="overflow"]'
    );
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot
      ?.querySelector('[part="overflow-list"]')
      ?.matches(":hover") ?? false;
  }

  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector(
      '[slot="overflow"] [ref="menuitem"]'
    );

    return menuItem instanceof HTMLElement
      ? menuItem
      : null;
  }

  get headerComponent() {
    return this.closest("header-component");
  }

  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const listItem = item.closest(".menu-list__list-item");

    const slot = listItem instanceof HTMLElement
      ? listItem.slot
      : "";

    const isMoreTrigger = slot === "more";
    const isDefaultSlot = slot === "";

    const overflowItem = isMoreTrigger
      ? this.#getFirstOverflowMenuItem()
      : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem =
      this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded =
        "false";

      const previousSubmenu = findSubmenu(previouslyActiveItem);

      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.inert = true;
      }
    }

    if (
      previouslyActiveOverflowItem &&
      previouslyActiveOverflowItem !== previouslyActiveItem
    ) {
      this.#expandableFor(
        previouslyActiveOverflowItem
      ).ariaExpanded = "false";

      const previousOverflowSubmenu = findSubmenu(
        previouslyActiveOverflowItem
      );

      if (previousOverflowSubmenu) {
        delete previousOverflowSubmenu.dataset.active;
        previousOverflowSubmenu.inert = true;
      }
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;

    this.ariaExpanded = "true";
    this.#expandableFor(item).ariaExpanded = "true";

    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = "true";

      const activeOverflowSubmenu = findSubmenu(overflowItem);

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = false;
      }
    }

    const overflowItemSubmenu = isMoreTrigger
      ? findSubmenu(overflowItem)
      : null;

    let submenu =
      findSubmenu(item) ||
      (overflowItemSubmenu ? this.overflowMenu : null);

    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;

      const committedItem = item;

      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;

          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      submenu.dataset.active = "";
      submenu.inert = false;

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                "--submenu-height",
                `${submenu.offsetHeight}px`
              );

              this.#cleanupMutationObserver();
            }
          });
        });
      });

      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;

        finalHeight = Math.max(
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      "--submenu-height",
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty("--submenu-opacity", "1");

    this.#startPointerTracking(item, previouslyActiveItem);
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target.contains(event.relatedTarget);

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(event.relatedTarget);

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === "blur" &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element &&
      Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (
      isMovingWithinItem ||
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }

      return;
    }

    this.#deactivate();
  }

  #deactivate = (
    item = this.#state.activeItem,
    { force = false } = {}
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem ||
      (
        !force &&
        (
          this.overflowListHovered ||
          this.overflowMenu?.matches(":hover")
        )
      )
    ) {
      return;
    }

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty(
      "--submenu-height",
      "0px"
    );

    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    const submenu =
      findSubmenu(item) ||
      (
        item.closest('[slot="more"]')
          ? this.overflowMenu
          : null
      );

    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener(
      "pointermove",
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;

    this.ariaExpanded = "false";
    this.#expandableFor(item).ariaExpanded = "false";

    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded =
        "false";

      const activeOverflowSubmenu = findSubmenu(
        activeOverflowItem
      );

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = true;
      }
    }

    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector("slot")
      ?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = link.querySelector('[ref="submenu[]"]');

        if (submenu) {
          callback(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty("display", "none");
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty("display");
    });

    return height;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    if (
      this.headerComponent.hasAttribute(
        "data-submenu-overlap-bottom-row"
      ) &&
      this.headerComponent.offsetHeight > 0
    ) {
      return this.headerComponent.querySelector(
        ".header__row--top"
      )?.offsetHeight ?? 0;
    }

    return this.headerComponent.offsetHeight;
  }

  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const fullOpenHeaderHeight =
      submenuHeight === 0
        ? 0
        : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty(
      "--full-open-header-height",
      `${fullOpenHeaderHeight}px`
    );
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach(
      (image) => image.removeAttribute("loading")
    );
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return (
    element.closest(".menu-list__list-item") ?? element
  ).querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element
    ?.closest?.(".menu-list__list-item")
    ?.querySelector(':scope > [ref="submenu[]"]');

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}